// ----------------------------------------------------------------------

// Why a request failed, phrased for the second line of an error toast.
//
// Four shapes have to be covered because all four reach the UI: this backend answers with
// `{ errorCode, description }`, ASP.NET's own failures come back as ProblemDetails
// (`title` / `detail`, plus a validation `errors` map), a proxy may answer with a bare string, and a
// request that never reached the server has only axios's `message`. Callers shouldn't have to know
// which one they got.
//
// Returns `undefined` when there is nothing worth saying, and sonner skips an undefined
// description — so passing this through is always safe, and a toast with no detail available reads
// exactly as it did before.

const MAX_LENGTH = 240;

const STATUS_TEXT = {
  400: 'The server rejected the request as invalid.',
  401: 'Your session is no longer valid.',
  403: "Your role doesn't hold the policy this endpoint requires.",
  404: 'Not found.',
  409: 'This conflicts with something that already exists.',
  413: 'The payload is too large.',
  422: 'The server could not process the request.',
  429: 'Too many requests — try again shortly.',
  500: 'The server hit an unhandled error.',
  502: 'The gateway could not reach the service.',
  503: 'The service is unavailable.',
  504: 'The service timed out.',
};

export function getErrorDescription(error) {
  if (!error) {
    return undefined;
  }

  const fromBody = readBody(error.response?.data);

  if (fromBody) {
    return clamp(fromBody);
  }

  const status = error.response?.status;

  if (status) {
    return clamp(statusLine(status, error.response?.statusText));
  }

  // No response at all: offline, DNS, CORS, a timeout, or an aborted request.
  return error.message ? clamp(error.message) : undefined;
}

// For `toast.promise`, whose `error` slot is the message itself rather than a line beneath it: the
// server's reason replaces the generic text, falling back to it when there is none.
export function errorMessage(error, fallback) {
  return getErrorDescription(error) ?? fallback;
}

// ----------------------------------------------------------------------

function readBody(data) {
  if (!data) {
    return null;
  }

  if (typeof data === 'string') {
    // Gateways and dev servers answer with HTML error pages, and a page of markup is not a message.
    const trimmed = data.trim();
    return trimmed && !trimmed.startsWith('<') ? trimmed : null;
  }

  if (typeof data !== 'object') {
    return null;
  }

  const direct = [data.description, data.detail, data.message, data.title, data.error].find(
    (value) => typeof value === 'string' && value.trim()
  );

  if (direct) {
    return direct.trim();
  }

  // ProblemDetails validation shape: `{ errors: { Field: ['message', ...] } }`.
  if (data.errors && typeof data.errors === 'object') {
    const lines = Object.entries(data.errors).map(
      ([field, messages]) => `${field}: ${[].concat(messages).join(', ')}`
    );

    if (lines.length) {
      return lines.join(' · ');
    }
  }

  return null;
}

// The status is only worth printing when the body said nothing — then it's the only clue there is.
function statusLine(status, phrase) {
  const known = STATUS_TEXT[status];

  if (known) {
    return `${status} · ${known}`;
  }

  return `Request failed with status ${status}${phrase ? ` (${phrase})` : ''}.`;
}

// Collapses whitespace before clamping, so a stack trace or a multi-line body can't take over the
// screen but still says something.
function clamp(text) {
  const single = String(text).replace(/\s+/g, ' ').trim();

  return single.length > MAX_LENGTH ? `${single.slice(0, MAX_LENGTH - 1)}…` : single;
}
